import { SimplePagination, Table } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ConversationsTableConversationPlaceholderRows } from './components/rows/conversationPlaceholderRows/conversationsTableConversationPlaceholderRows';
import { ConversationsTableConversationRow } from './components/rows/conversationRow/conversationsTableConversationRow';
import { ConversationsTableEmptyRow } from './components/rows/emptyRow/conversationsTableEmptyRow';
import { ConversationsTableHeadRow } from './components/rows/headRow/conversationsTableHeadRow';
import { ConversationsTableRejectedRow } from './components/rows/rejectedRow/conversationsTableRejectedRow';
import styles from './conversationsTable.module.scss';
import { type ConversationsTableProps } from './conversationsTable.types';

/** Represents the conversations table component. */
export const ConversationsTable = ({
  conversations,
  asyncStatus,
  selectableRowsProps,
  paginationProps,
  sortingProps,
  onRowClick,
  ...tableProps
}: ConversationsTableProps): JSX.Element => {
  const [isInitialized, setIsInitialized] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const scrollTableToTop = useCallback(() => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTop = 0;
    }
  }, []);

  const tableBody = useMemo(() => {
    if (asyncStatus === 'uninitialized' || asyncStatus === 'pending') {
      return <ConversationsTableConversationPlaceholderRows />;
    }

    if (asyncStatus === 'rejected') {
      return <ConversationsTableRejectedRow />;
    }

    if (!conversations.length) {
      return <ConversationsTableEmptyRow />;
    }

    return (
      <>
        {conversations.map((conversation) => (
          <ConversationsTableConversationRow
            key={conversation.id}
            conversation={conversation}
            onRowClick={onRowClick}
          />
        ))}
      </>
    );
  }, [conversations, asyncStatus]);

  // Handles scrolling to the top of the table when there are new items.
  useEffect(() => {
    if (asyncStatus === 'fulfilled' || asyncStatus === 'rejected') {
      scrollTableToTop();

      // Tells that the state has been through the first cycle.
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [asyncStatus, isInitialized, scrollTableToTop]);

  return (
    <div className={styles.root}>
      <Table
        stickyHeader
        hoverable={asyncStatus === 'fulfilled' && conversations.length > 0}
        loading={asyncStatus === 'uninitialized' || asyncStatus === 'pending'}
        loadingStrategy="disabled"
        wrapperRef={tableWrapperRef}
        className={classnames(styles.table, {
          [styles.tableExpanded]:
            (asyncStatus === 'fulfilled' && !conversations.length) ||
            asyncStatus === 'rejected',
        })}
        selectableRowsProps={selectableRowsProps}
        sortingProps={sortingProps}
        {...tableProps}
      >
        <Table.Thead className={styles.thead}>
          <ConversationsTableHeadRow isInitialized={isInitialized} />
        </Table.Thead>

        <Table.Tbody>{tableBody}</Table.Tbody>

        <Table.Footer>
          <SimplePagination {...paginationProps} />
        </Table.Footer>
      </Table>
    </div>
  );
};
